import { readingCandidateSchema } from "@bindery/orientation/contracts";
import {
  CORPUS_VOCAB_SIZE,
  MAX_EXCLUDE_TEXT_IDS,
  MAX_EXTRA_WORDS,
  MAX_WORD_LENGTH,
} from "@bindery/shared/corpus/selection";
import * as z from "zod";
import { READING_PATH_PREFIX } from "./reading";

export const READING_RECOMMENDATIONS_PATH = `${READING_PATH_PREFIX}recommendations`;

export const DEFAULT_RECOMMENDATION_LIMIT = 20;
const MAX_RECOMMENDATION_LIMIT = 100;

const normalizedWordSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(1)
  .max(MAX_WORD_LENGTH);

const wordListSchema = (max: number) =>
  z.array(normalizedWordSchema).max(max).default([]);

// Words, not word ids: the reader's vocabulary file and
// production_texts.vocab are not yet in the same word order (kanban #166),
// so ids built on the client would be scored as other words. Resolving
// headwords on the server is correct before and after the renumber.
export const readingRecommendationsRequestSchema = z
  .strictObject({
    knownWords: wordListSchema(CORPUS_VOCAB_SIZE + MAX_EXTRA_WORDS),
    predictedWords: wordListSchema(CORPUS_VOCAB_SIZE),
    /** P(known) per predicted word, parallel to predictedWords. */
    predictedConfidences: z
      .array(z.number().min(0).max(1))
      .max(CORPUS_VOCAB_SIZE)
      .optional(),
    developingWords: wordListSchema(CORPUS_VOCAB_SIZE),
    excludeTextIds: z.array(z.uuid()).max(MAX_EXCLUDE_TEXT_IDS).default([]),
    limit: z
      .number()
      .int()
      .min(1)
      .max(MAX_RECOMMENDATION_LIMIT)
      .default(DEFAULT_RECOMMENDATION_LIMIT),
  })
  .refine(
    (data) =>
      data.predictedConfidences === undefined ||
      data.predictedConfidences.length === data.predictedWords.length,
    {
      message:
        "predictedConfidences array length must match predictedWords length",
      path: ["predictedConfidences"],
    },
  );
export type ReadingRecommendationsRequest = z.input<
  typeof readingRecommendationsRequestSchema
>;
export type ParsedReadingRecommendationsRequest = z.output<
  typeof readingRecommendationsRequestSchema
>;

export const readingRecommendationsResponseSchema = z.object({
  candidates: z.array(readingCandidateSchema),
});
export type ReadingRecommendationsResponse = z.infer<
  typeof readingRecommendationsResponseSchema
>;

export const READING_RECOMMENDATIONS_ERRORS = [
  "unauthenticated",
  "not_reader",
  "invalid_request",
  "cross_site",
  "method_not_allowed",
  "unconfigured",
  "upstream_failed",
] as const;

export const readingRecommendationsErrorSchema = z.object({
  error: z.enum(READING_RECOMMENDATIONS_ERRORS),
});
export type ReadingRecommendationsError = z.infer<
  typeof readingRecommendationsErrorSchema
>;
